import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { Notice } from '@/components/admin/Notice';
import * as authApi from '@/features/auth/api';
import { ApiError } from '@/lib/api/http';

/**
 * 事務局・スーパー管理者専用のログイン画面（`/staff/login`）。
 * 受講生・理事・講師・サクセス担当は `/login` を使う（利用者フィードバックにより
 * ログイン導線を分離。指示書 §10-3 の「専用ログイン」に対応）。
 */
export function StaffLoginPage(): ReactElement {
  const { user, needsTotp, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 通常はGoogleログイン。認証アプリを失ったときの緊急手段だけ折りたたんで置く（D-068）
  const [showBreakglass, setShowBreakglass] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(needsTotp ? '/totp' : '/', { replace: true });
  }, [user, needsTotp, navigate]);

  async function handleGoogleLogin(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const result = await authApi.loginWithGoogleMock(email);
      setUser(result.user);
      navigate(result.user.totp_required && !result.user.totp_verified ? '/totp' : '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ログインに失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasswordLogin(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const result = await authApi.login(email, password);
      setUser(result.user);
      navigate(result.user.totp_required && !result.user.totp_verified ? '/totp' : '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ログインに失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wpcanvas px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-center text-xl font-semibold text-wptext-strong">CXO GROWTH</h1>
        <p className="mb-6 text-center text-wptext-muted">事務局・スーパー管理者ログイン</p>

        <div className="rounded-sm border border-wpborder bg-white px-5 py-6 shadow-sm">
          {error && (
            <Notice kind="error" dismissible={false}>
              {error}
            </Notice>
          )}

          <label htmlFor="staff-login-email" className="mb-1.5 block text-wptext-muted">
            メールアドレス
          </label>
          <input
            id="staff-login-email"
            name="email"
            type="text"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mb-3 w-full rounded-sm border border-wpborder px-3 py-2"
          />

          <p className="mb-3 text-xs text-wptext-muted">
            パスワードは使いません。上のメールアドレスを入力し、下のボタンから
            Googleアカウントでログインしてください。続けて2要素認証（TOTP）の
            コード入力画面に進みます（初回は認証アプリの設定から始まります）。
          </p>

          <button
            type="button"
            disabled={submitting || !email}
            onClick={() => void handleGoogleLogin()}
            className="btn-secondary w-full py-2"
          >
            Google でログイン
          </button>
          {!email && (
            <p className="mt-1 text-xs text-wptext-muted">
              ↑ 先にメールアドレスを入力するとボタンを押せます
            </p>
          )}
          <p className="mt-2 text-xs text-wptext-muted">
            ※ 現在は開発用の簡易ログインです。押すとGoogleの画面は表示されず、上で
            入力したメールアドレスのままログインします。本番では実際のGoogleログイン
            画面に進む形に切り替わります。
          </p>
          <div className="mt-4 border-t border-wpborder pt-3">
            <button
              type="button"
              onClick={() => setShowBreakglass((prev) => !prev)}
              className="text-xs text-wpaccent hover:underline"
            >
              認証アプリが使えない場合（許可された場所からのみ）
            </button>
            {showBreakglass && (
              <div className="mt-2">
                <p className="mb-2 text-xs text-wptext-muted">
                  事務局が許可したネットワーク（固定IP）からに限り、メールアドレスと
                  パスワードでログインできます。それ以外の場所からは、この方法でも
                  2要素認証のコード入力が必要です。利用は記録されます。
                </p>
                <label htmlFor="staff-login-password" className="mb-1.5 block text-wptext-muted">
                  パスワード
                </label>
                <input
                  id="staff-login-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mb-2 w-full rounded-sm border border-wpborder px-3 py-2"
                />
                <button
                  type="button"
                  disabled={submitting || !email || !password}
                  onClick={() => void handlePasswordLogin()}
                  className="btn-secondary w-full py-2"
                >
                  メールアドレスとパスワードでログイン
                </button>
                <p className="mt-2 text-xs text-wptext-muted">
                  パスワードを設定していない・忘れた場合は
                  <Link to="/password/reset-request" className="text-wpaccent hover:underline">
                    こちら
                  </Link>
                  から設定できます。
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-wptext-muted">
          受講生・理事・講師・サクセス担当の方は
          <Link to="/login" className="text-wpaccent hover:underline">
            こちら
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-wptext-muted">
          アカウントは招待制です。お持ちでない場合は事務局にご連絡ください。
        </p>
      </div>
    </div>
  );
}
